import { VOICE_RECORDING_STATUSES } from '@/shared-responder-v2-core';
import {
  DICTATION_STATES,
  type DictationState,
} from '@/shared-responder-v2-shell';

import type { ResponderDictationControl } from './shared-responder-v2-capability-coordinator.hooks';
import {
  resolveSpeechRecognitionConstructor,
  type SpeechRecognitionLike,
} from './shared-responder-v2-dictation-speech.types';
import {
  DICTATION_PHASES,
  type DictationPhase,
} from './shared-responder-v2-dictation.consts';

const UNAVAILABLE_PHASES: ReadonlySet<DictationPhase> = new Set([
  DICTATION_PHASES.Unsupported,
  DICTATION_PHASES.PermissionDenied,
  DICTATION_PHASES.NoInputDevice,
]);

const ACTIVE_PHASES: ReadonlySet<DictationPhase> = new Set([
  DICTATION_PHASES.Listening,
  DICTATION_PHASES.RequestingPermission,
]);

const BLOCKED_VOICE_STATUSES: ReadonlySet<string> = new Set([
  VOICE_RECORDING_STATUSES.Denied,
  VOICE_RECORDING_STATUSES.Unsupported,
]);

const PERMISSION_DENIED_ERROR_NAMES: ReadonlySet<string> = new Set([
  'NotAllowedError',
  'PermissionDeniedError',
  'SecurityError',
]);

const NO_INPUT_DEVICE_ERROR_NAMES: ReadonlySet<string> = new Set([
  'NotFoundError',
  'DevicesNotFoundError',
  'OverconstrainedError',
]);

export function subscribeToNothing(): () => void {
  return () => undefined;
}

export function isSpeechRecognitionSupported(): boolean {
  return resolveSpeechRecognitionConstructor() !== null;
}

export function isSpeechRecognitionSupportedOnServer(): boolean {
  return false;
}

export function resolveDictationState(phase: DictationPhase): DictationState {
  if (UNAVAILABLE_PHASES.has(phase)) {
    return DICTATION_STATES.Unavailable;
  }

  return phase === DICTATION_PHASES.Listening
    ? DICTATION_STATES.Listening
    : DICTATION_STATES.Idle;
}

/**
 * A relayed session's recogniser runs on the phone, so this page has no phase of
 * its own to report. It reads as listening because that is what the responder
 * sees: the composer's live state, with nothing here to start or stop.
 */
export function resolveRemoteDictationState(): DictationState {
  return DICTATION_STATES.Listening;
}

export function isDictatingPhase(phase: DictationPhase): boolean {
  return ACTIVE_PHASES.has(phase);
}

export function toggleDictation(control: ResponderDictationControl): void {
  if (control.isDictating) {
    control.stop();
  } else {
    control.start();
  }
}

/**
 * A microphone the responder refused, or one the browser cannot open at all, is
 * a permanent condition for the session: the composer says so once and the
 * responder types instead. A device that merely dropped out is not listed — the
 * lifecycle retries that on its own.
 */
export function isVoiceCaptureBlocked(status: string): boolean {
  return BLOCKED_VOICE_STATUSES.has(status);
}

/**
 * A listening port already holds and meters the same physical microphone, so a
 * second capture would measure the same sound twice — and in a hands-free user
 * test the screen recording holds a third. Its grant also stands in for the
 * permission probe: a port cannot be listening without one.
 */
export function canShareVoicePortMicrophone(port: {
  readonly status: string;
  readonly audioLevel?: number;
}): boolean {
  return (
    port.status === VOICE_RECORDING_STATUSES.Listening &&
    port.audioLevel !== undefined
  );
}

/**
 * Whether the recogniser should be started for the responder rather than by
 * them.
 *
 * Only ever true from `Idle`: `Listening` is already live, and `Unavailable` is
 * a refused or missing device, which a re-arm would turn into a fresh
 * permission prompt at every question boundary.
 */
export function shouldRearmDictation(args: {
  readonly isArmed: boolean;
  readonly isMicrophoneBlocked: boolean;
  readonly state: DictationState;
}): boolean {
  if (!args.isArmed || args.isMicrophoneBlocked) {
    return false;
  }

  return args.state === DICTATION_STATES.Idle;
}

export function canStartDictation(args: {
  readonly phase: DictationPhase;
  readonly isPlaybackActive: boolean;
  /** Held by the inactivity pause, whose card is the only thing on screen. */
  readonly isSessionPaused: boolean;
}): boolean {
  if (args.isPlaybackActive || args.isSessionPaused) {
    return false;
  }

  return !isDictatingPhase(args.phase);
}

export function extractErrorName(error: unknown): string {
  if (error instanceof DOMException) {
    return error.name;
  }

  if (error instanceof Error) {
    return error.name;
  }

  return 'UnknownError';
}

export function mapMediaErrorToPhase(errorName: string): DictationPhase {
  if (PERMISSION_DENIED_ERROR_NAMES.has(errorName)) {
    return DICTATION_PHASES.PermissionDenied;
  }

  if (NO_INPUT_DEVICE_ERROR_NAMES.has(errorName)) {
    return DICTATION_PHASES.NoInputDevice;
  }

  return DICTATION_PHASES.RecognitionFailed;
}

export function mapSpeechRecognitionErrorToPhase(
  errorCode: string
): DictationPhase | null {
  if (errorCode === 'not-allowed' || errorCode === 'service-not-allowed') {
    return DICTATION_PHASES.PermissionDenied;
  }

  if (errorCode === 'audio-capture') {
    return DICTATION_PHASES.NoInputDevice;
  }

  if (errorCode === 'no-speech' || errorCode === 'aborted') {
    return null;
  }

  return DICTATION_PHASES.RecognitionFailed;
}

export function joinTranscript(base: string, addition: string): string {
  const trimmedAddition = addition.trim();

  if (trimmedAddition === '') {
    return base;
  }

  return base === '' ? trimmedAddition : `${base} ${trimmedAddition}`;
}

export function composeDraftText(
  committedText: string,
  interimText: string
): string {
  return joinTranscript(committedText, interimText);
}

export interface TranscriptResult {
  readonly isFinal: boolean;
  readonly transcript: string;
}

export interface TranscriptExtraction {
  readonly finalChunk: string;
  readonly interimChunk: string;
}

interface SpeechResultLike {
  readonly isFinal: boolean;
  item(index: number): { readonly transcript: string };
}

export interface SpeechResultListLike {
  readonly length: number;
  item(index: number): SpeechResultLike;
}

export function toTranscriptResults(
  results: SpeechResultListLike
): TranscriptResult[] {
  const collected: TranscriptResult[] = [];

  for (let index = 0; index < results.length; index += 1) {
    const result = results.item(index);
    collected.push({
      isFinal: result.isFinal,
      transcript: result.item(0).transcript,
    });
  }

  return collected;
}

export function extractTranscriptChunks(
  results: readonly TranscriptResult[],
  startIndex: number
): TranscriptExtraction {
  let finalChunk = '';
  let interimChunk = '';

  for (let index = startIndex; index < results.length; index += 1) {
    const result = results[index];

    if (result.isFinal) {
      finalChunk = joinTranscript(finalChunk, result.transcript);
    } else {
      interimChunk = joinTranscript(interimChunk, result.transcript);
    }
  }

  return { finalChunk, interimChunk };
}

interface ReleasableCaptureStream {
  readonly getTracks: () => readonly { readonly stop: () => void }[];
}

export function stopMediaStream(stream: ReleasableCaptureStream | null): void {
  for (const track of stream?.getTracks() ?? []) track.stop();
}

export function detachSpeechRecognition(
  recognition: SpeechRecognitionLike | null
): void {
  if (recognition === null) return;

  // oxlint-disable-next-line unicorn/prefer-add-event-listener
  recognition.onresult = null;
  // oxlint-disable-next-line unicorn/prefer-add-event-listener
  recognition.onerror = null;
  // oxlint-disable-next-line unicorn/prefer-add-event-listener
  recognition.onend = null;
  recognition.onstart = null;
  try {
    recognition.stop();
  } catch {
    try {
      recognition.abort();
    } catch {
      return;
    }
  }
}
